import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import ComplaintView from '../../complaints/components/ComplaintView';
import DeviceQRCode from './DeviceQRCode';
import DeviceMaintenanceScheduleModal from './DeviceMaintenanceScheduleModal';
import { DeviceIDBadge, StatusBadge, SeverityBadge, ComplaintIDBadge } from '../../../shared/components/common/Badges';
import { deviceHasAvailabilityImpact } from '../../../shared/constants/availabilityImpactFilter';
import { formatDepartmentWithIcon } from '../../admin/utils/departmentUtils';
import { useRoleAccess } from '../../../hooks/useRoleAccess';
import ExportButton from '../../../shared/components/modals/ExportButton';
import ExportColumnConfigurationModal from '../../../shared/components/modals/ExportColumnConfigurationModal';
import { formatDocumentType } from '../../../shared/components/common/DocumentTypes';
import ActionMenu from '../../../shared/components/common/ActionMenu';
import {
  createDeviceExportColumnsState,
  getSelectedExportColumnHeaders,
} from '../constants/deviceExportColumns';
import {
  PDF_EXPORT_MAX_COLUMNS,
  validatePdfExportColumnCount,
} from '../../../constants/exportColumnPolicy';
import { getDeviceAgeParts, formatDeviceAgeText } from '../../../utils/dateUtils';
import { formatCurrencyDynamic } from '../../../utils/numberUtils';
import './DeviceList.css';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { ACTIVE_COMPLAINT_STATUSES } from '../../../utils/fleetMetricUtils';

function DeviceList({ devices, totalDevices, onEdit, onDelete, onView, onReportComplaint, onViewComplaint, token, hasFiltersApplied, onExportExcel, onExportPDF, onExportMaintenancePDF, user, isRefreshing, onViewMaintenanceHistory, onNavigateToAlertsWithFilter, sorting = null, onSortChange = null, selectedDeviceIds = new Set(), selectAllDevices = false, isSelectingAllDevices = false, onToggleDeviceSelection = null, onToggleSelectAll = null, exportTotalCount = null, children = null }) {
  // SORTING: Now managed by parent (DevicesPage.js) for server-side sorting
  // sorting object contains: { sort_by, sort_direction }
  // Call onSortChange(column) to update parent sorting state

  const selectAllCheckboxRef = useRef(null);

  const allMatchingSelected = selectAllDevices || (
    totalDevices > 0 && selectedDeviceIds && selectedDeviceIds.size >= totalDevices
  );
  const someDevicesSelected = selectedDeviceIds && selectedDeviceIds.size > 0;

  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = Boolean(
        someDevicesSelected && !allMatchingSelected && !isSelectingAllDevices
      );
    }
  }, [someDevicesSelected, allMatchingSelected, isSelectingAllDevices, selectedDeviceIds?.size, totalDevices]);

  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [selectedDeviceDocuments, setSelectedDeviceDocuments] = useState([]);
  const [selectedDeviceForDocuments, setSelectedDeviceForDocuments] = useState(null);
  const [selectedDocumentForPreview, setSelectedDocumentForPreview] = useState(null);
  const [documentPreviewUrlInModal, setDocumentPreviewUrlInModal] = useState(null);
  const [previewLoadingInModal, setPreviewLoadingInModal] = useState(false);
  const [showComplaintsModal, setShowComplaintsModal] = useState(false);
  const [selectedDeviceComplaints, setSelectedDeviceComplaints] = useState([]);
  const [selectedDeviceName, setSelectedDeviceName] = useState('');
  const [selectedDeviceIdentifier, setSelectedDeviceIdentifier] = useState('');
  const [selectedComplaintForDetail, setSelectedComplaintForDetail] = useState(null);
  const [showExportColumnsConfig, setShowExportColumnsConfig] = useState(false);
  const [showNextDueModal, setShowNextDueModal] = useState(false);
  const [selectedDeviceForNextDue, setSelectedDeviceForNextDue] = useState(null);
  const [showQRCode, setShowQRCode] = useState(false);
  const [qrDevice, setQRDevice] = useState(null);
  const [exportColumns, setExportColumns] = useState(() => createDeviceExportColumnsState('excel'));

  // Get role-based access control from hook
  const { canPerformFeature } = useRoleAccess(user);

  // RESTRICTED: Check if user can edit devices (admin and manager only - NOT coordinator)
  const canEdit = user && (user.role === 'admin' || user.role === 'manager');

  // Check if user can delete devices (admin only - compliance/audit requirement)
  const canDelete = user && user.role === 'admin';

  // Check if user can export devices (whitelist: superadmin, admin, coordinator, custodian, manager)
  const canExportDevices = canPerformFeature('export_devices');

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return { date: 'N/A', time: '' };
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const handleAttachmentClick = async (e, device) => {
    e.stopPropagation();

    if (!token) return;

    try {
      const response = await axios.get(
        API_ENDPOINTS.DEVICES.DOCUMENTS(device.id),
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      setSelectedDeviceDocuments(response.data);
      setSelectedDeviceForDocuments(device);
      setShowDocumentsModal(true);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to fetch documents');
    }
  };

  const handleCloseDocumentsModal = () => {
    setShowDocumentsModal(false);
    setSelectedDeviceDocuments([]);
    setSelectedDeviceForDocuments(null);
    setSelectedDocumentForPreview(null);
    setDocumentPreviewUrlInModal(null);
  };

  const handlePreviewDocumentInModal = async (doc, deviceId) => {
    setSelectedDocumentForPreview(doc);
    setPreviewLoadingInModal(true);

    try {
      const response = await axios.get(
        API_ENDPOINTS.DOCUMENTS.GET(deviceId, doc.id),
        {
          headers: { 'Authorization': `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // response.data is already a Blob, use it directly
      const url = window.URL.createObjectURL(response.data);
      setDocumentPreviewUrlInModal(url);
    } catch (error) {
      console.error('Error fetching document preview:', error);
      toast.error('Failed to load document preview');
    } finally {
      setPreviewLoadingInModal(false);
    }
  };


  const handleComplaintClick = async (e, device) => {
    e.stopPropagation();

    if (!token) return;

    try {
      const response = await axios.get(
        API_ENDPOINTS.COMPLAINTS.LIST,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      const severityRank = { critical: 0, major: 1, minor: 2 };
      const deviceComplaints = response.data
        .filter(
          (c) => c.device_id === device.id && ACTIVE_COMPLAINT_STATUSES.includes(c.status)
        )
        .sort((a, b) => {
          const bySeverity =
            (severityRank[(a.severity || 'minor').toLowerCase()] ?? 9) -
            (severityRank[(b.severity || 'minor').toLowerCase()] ?? 9);
          if (bySeverity !== 0) return bySeverity;
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        });
      setSelectedDeviceComplaints(deviceComplaints);
      setSelectedDeviceName(device.name);
      setSelectedDeviceIdentifier(device.device_identifier || '');
      setShowComplaintsModal(true);
    } catch (error) {
      console.error('Error fetching complaints:', error);
      toast.error('Failed to fetch complaints');
    }
  };

  const handleCloseComplaintsModal = () => {
    setShowComplaintsModal(false);
    setSelectedDeviceComplaints([]);
    setSelectedDeviceName('');
    setSelectedDeviceIdentifier('');
  };

  const handleComplaintItemClick = (complaint) => {
    setSelectedComplaintForDetail(complaint);
  };

  const handleCloseComplaintDetail = () => {
    setSelectedComplaintForDetail(null);
  };

  const handleNextDueClick = (e, device) => {
    e.stopPropagation();
    setSelectedDeviceForNextDue(device);
    setShowNextDueModal(true);
  };

  const handleCloseNextDueModal = () => {
    setShowNextDueModal(false);
    setSelectedDeviceForNextDue(null);
  };

  // Handle Escape key to close Documents and Complaints modals
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showDocumentsModal) {
          handleCloseDocumentsModal();
        } else if (showComplaintsModal) {
          handleCloseComplaintsModal();
        }
      }
    };

    if (showDocumentsModal || showComplaintsModal) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showDocumentsModal, showComplaintsModal]);

  // Prevent body scroll when any modal is open
  useEffect(() => {
    if (showDocumentsModal || showComplaintsModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showDocumentsModal, showComplaintsModal]);

  const getDocIcon = (mimeType) => {
    if (mimeType.includes('pdf')) return '📄';
    if (mimeType.includes('image')) return '🖼️';
    if (mimeType.includes('word')) return '📝';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
    return '📎';
  };

  const getContractTypeIcon = (contractType) => {
    const iconMap = {
      'Warranty': { icon: '🛡️', label: 'Warranty' },
      'LAMC': { icon: '🔧', label: 'LAMC' },
      'CAMC': { icon: '⚙️', label: 'CAMC' },
      'In-house': { icon: '🏢', label: 'In-house' }
    };
    return iconMap[contractType] || { icon: '❓', label: contractType };
  };

  // ═══════════════════════════════════════════════════════════════════
  // SORTING LOGIC (Server-side)
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Handle column header click to sort
   * Calls parent onSortChange handler which updates server-side sorting
   */
  const handleColumnSort = (column) => {
    if (onSortChange) {
      onSortChange(column);
    }
  };

  /**
   * Get sort indicator for column header
   * Uses parent sorting state to determine active sort column and direction
   */
  const getSortIndicator = (column) => {
    if (!sorting || sorting.sort_by !== column) return '';
    return sorting.sort_direction === 'asc' ? ' ▲' : ' ▼';
  };

  const handleExportPDF = () => {
    const selected = getSelectedExportColumnHeaders(exportColumns);
    if (selected.length === 0) {
      toast.error('Select at least one column to export');
      return;
    }
    const pdfCheck = validatePdfExportColumnCount(selected.length);
    if (!pdfCheck.ok) {
      toast.error(pdfCheck.message);
      return;
    }
    if (onExportPDF) {
      onExportPDF(selected);
    }
  };

  const handleExportExcel = () => {
    const selected = getSelectedExportColumnHeaders(exportColumns);
    if (selected.length === 0) {
      toast.error('Select at least one column to export');
      return;
    }
    if (onExportExcel) {
      onExportExcel(selected);
    }
  };

  const exportCount = exportTotalCount != null ? exportTotalCount : totalDevices;

  const getDeviceActions = (device) => {
    const isInTransfer = device.device_status === 'in_transfer';
    const canReportComplaint = !isInTransfer;

    return [
      {
        id: 'edit',
        icon: '✎',
        label: 'Edit',
        disabled: !canEdit,
        title: !canEdit ? 'Only administrators and managers can edit devices' : 'Edit device specs, maintenance, and contracts',
        onClick: () => {
          if (canEdit) onEdit(device);
        }
      },
      {
        id: 'qr-code',
        icon: '◈',
        label: 'QR Code',
        onClick: () => {
          setQRDevice(device);
          setShowQRCode(true);
        }
      },
      {
        id: 'report',
        icon: '⚠',
        label: 'Report',
        disabled: !canReportComplaint,
        title: !canReportComplaint
          ? 'Complaints cannot be created while this device is in a transfer'
          : 'Report a complaint for this device',
        onClick: () => {
          if (canReportComplaint) onReportComplaint(device);
        }
      },
      {
        id: 'maintenance-history',
        icon: '⚙️',
        label: 'Maintenance History',
        disabled: !(onViewMaintenanceHistory && device.device_identifier),
        onClick: () => {
          if (onViewMaintenanceHistory && device.device_identifier) {
            onViewMaintenanceHistory({ device_identifier: device.device_identifier });
          }
        }
      },
      {
        id: 'delete',
        icon: '✕',
        label: 'Delete',
        danger: true,
        disabled: !canDelete,
        title: !canDelete ? 'Only administrators can delete devices (compliance requirement)' : 'Delete device',
        onClick: () => {
          if (canDelete) onDelete(device.id, device.name);
        }
      }
    ];
  };

  if (!devices || devices.length === 0) {
    return (
      <div className="empty-state">
        <p>
          {hasFiltersApplied
            ? 'No devices found. Please refine your search.'
            : 'No devices found. Add your first device to get started!'}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="device-list">
        {/* Device Count Info - Above Export Section */}
        <div className="device-count-section">
          Showing {devices.length} of {totalDevices} devices
          {canExportDevices && exportCount > 0 && (
            <span className="device-export-hint"> — Export includes all {exportCount} filtered devices</span>
          )}
          {onToggleDeviceSelection && !someDevicesSelected && (
            <span className="device-qr-select-hint">
              Tip: use the QR checkboxes on the right to select devices, then print QR labels
            </span>
          )}
        </div>

        {/* Export Dropdowns - Above Table */}
        {canExportDevices && (
        <div className="device-list-export-section">
          <div className="export-buttons-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {/* Export Button Component */}
            <ExportButton
              onExportPDF={handleExportPDF}
              onExportExcel={handleExportExcel}
              title="Export devices"
            />

            {/* Configure Export Columns Button */}
            <button
              style={{
                background: 'none',
                border: 'none',
                padding: '0.25rem',
                cursor: 'pointer',
                fontSize: '1.5rem',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: '1'
              }}
              onClick={() => setShowExportColumnsConfig(true)}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.15)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
              }}
              title="Configure export columns"
            >
              ⚙️
            </button>
          </div>

        </div>
        )}

        {!canExportDevices && (
          <div className="device-list-export-section">
            <div style={{ padding: '12px 15px', fontSize: '0.9rem', color: '#666', backgroundColor: '#f5f5f5', borderRadius: '4px', border: '1px solid #ddd' }}>
              ℹ️ Export functionality is not available for your role
            </div>
          </div>
        )}

        <div className="device-table-container" style={{ position: 'relative' }}>
          {isRefreshing && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              borderRadius: '4px'
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1rem'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  border: '4px solid #e5e7eb',
                  borderTop: '4px solid var(--dp-accent-primary)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <div style={{ color: 'var(--dp-accent-primary)', fontWeight: '600', fontSize: '0.95rem' }}>
                  Refreshing devices...
                </div>
              </div>
            </div>
          )}
        <table className="device-table">
        <thead>
          <tr>
            <th
              className="device-col-header"
              onClick={() => handleColumnSort('name')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
              title="Click to sort by device name"
            >
              <div className="device-col-header-main">
                Device{getSortIndicator('name')}
              </div>
              <div
                className="device-col-legend"
                onClick={(e) => e.stopPropagation()}
              >
                <span
                  className="device-col-legend-item"
                  title="Circle = criticality — red Critical, amber Major, blue Minor"
                >
                  <span className="device-col-legend-swatches" aria-hidden="true">
                    <span className="device-signal device-signal-criticality device-signal-criticality--critical" />
                    <span className="device-signal device-signal-criticality device-signal-criticality--major" />
                    <span className="device-signal device-signal-criticality device-signal-criticality--minor" />
                  </span>
                  Criticality
                </span>
                <span
                  className="device-col-legend-item"
                  title="Purple square = clinical availability impacted by an active complaint"
                >
                  <span className="device-signal device-signal-impact" aria-hidden="true" />
                  Impacted
                </span>
              </div>
            </th>
            <th>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span
                  onClick={() => handleColumnSort('model')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Click to sort by model"
                >
                  Model{getSortIndicator('model')}
                </span>
                <span
                  onClick={() => handleColumnSort('manufacturer')}
                  style={{ cursor: 'pointer', userSelect: 'none', fontSize: '0.9em', marginTop: '4px' }}
                  title="Click to sort by manufacturer"
                >
                  Manufacturer{getSortIndicator('manufacturer')}
                </span>
              </div>
            </th>
            <th>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span
                  onClick={() => handleColumnSort('department')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Click to sort by department"
                >
                  Department{getSortIndicator('department')}
                </span>
                <span
                  onClick={() => handleColumnSort('physical_location')}
                  style={{ cursor: 'pointer', userSelect: 'none', fontSize: '0.9em', marginTop: '4px' }}
                  title="Click to sort by location"
                >
                  Physical Location{getSortIndicator('physical_location')}
                </span>
              </div>
            </th>
            <th
              onClick={() => handleColumnSort('contract')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
              title="Click to sort by contract type"
            >
              Contract Type{getSortIndicator('contract')}
            </th>
            <th className="device-cost-col-header">
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span
                  onClick={() => handleColumnSort('product_cost')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Click to sort by device cost"
                >
                  Device Cost{getSortIndicator('product_cost')}
                </span>
                <span
                  onClick={() => handleColumnSort('service_contract_cost')}
                  style={{ cursor: 'pointer', userSelect: 'none', fontSize: '0.9em', marginTop: '4px' }}
                  title="Click to sort by annual contract cost"
                >
                  Contract Cost{getSortIndicator('service_contract_cost')}
                </span>
              </div>
            </th>
            <th
              onClick={() => handleColumnSort('installation_date')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
              title="Click to sort by installation date"
            >
              Installation Date{getSortIndicator('installation_date')}
            </th>
            <th
              onClick={() => handleColumnSort('updated_at')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
              title="Click to sort by last updated date"
            >
              Last Updated{getSortIndicator('updated_at')}
            </th>
            <th>Maintenance Schedule</th>
            <th>Actions</th>
            {onToggleSelectAll && (
              <th className="device-qr-select-col" title="Select devices to print QR labels">
                <div className="device-qr-select-header">
                  <span className="device-qr-select-label">QR</span>
                  <input
                    ref={selectAllCheckboxRef}
                    type="checkbox"
                    checked={allMatchingSelected}
                    disabled={isSelectingAllDevices}
                    onChange={onToggleSelectAll}
                    aria-label="Select all devices for QR labels"
                    title={
                      isSelectingAllDevices
                        ? 'Selecting all devices matching current filters...'
                        : allMatchingSelected
                          ? 'Deselect all — clear QR label selection'
                          : 'Select all matching devices for QR labels (all pages)'
                    }
                  />
                </div>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {devices.map((device) => (
            <tr
              key={device.id}
            >
              <td className="device-name">
                <div className="device-name-section">
                  <div className="device-location">
                    📍 {device.location_name || 'N/A'}
                  </div>
                  {device.transferred_from_location_name && (
                    <span className="device-transferred-from-notice" role="status">
                      Transferred from {device.transferred_from_location_name}
                    </span>
                  )}
                  {device.device_status === 'in_transfer' && (
                    <span
                      className="device-in-transfer-notice"
                      role="status"
                      title="Complaints cannot be created while this device is in a transfer"
                    >
                      {device.transferring_to_location_name
                        ? `🚚 Transferring to ${device.transferring_to_location_name}`
                        : device.transferring_to_department_name
                          ? `🔁 Transferring to ${device.transferring_to_department_name}`
                          : 'In transfer'}
                    </span>
                  )}
                  <div className="device-name-primary-row">
                    <span className="device-name-text">
                      {device.name}
                      {(device.criticality || deviceHasAvailabilityImpact(device)) && (
                        <span className="device-signal-markers">
                          {device.criticality && (
                            <span
                              className={`device-signal device-signal-criticality device-signal-criticality--${String(device.criticality).toLowerCase()}`}
                              title={`Criticality: ${String(device.criticality).charAt(0).toUpperCase()}${String(device.criticality).slice(1).toLowerCase()}`}
                              aria-label={`Criticality: ${String(device.criticality).toLowerCase()}`}
                            />
                          )}
                          {deviceHasAvailabilityImpact(device) && (
                            <span
                              className="device-signal device-signal-impact"
                              title="Clinical availability is impacted by an active complaint"
                              aria-label="Clinical availability impacted"
                            />
                          )}
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="device-meta-row">
                    <DeviceIDBadge
                      deviceId={device.device_identifier || 'N/A'}
                      clickable={true}
                      onClick={(e) => {
                        e.stopPropagation();
                        onView(device);
                      }}
                      size="small"
                      title="Click to view device details"
                    />
                    <div className="device-indicators">
                      {device.active_complaint_count > 0 && (
                        <span
                          className="complaint-indicator clickable"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleComplaintClick(e, device);
                          }}
                          title="Click to view active complaints"
                        >
                          🔧
                          <span className="device-indicator-count device-indicator-count--complaint">
                            {device.active_complaint_count}
                          </span>
                        </span>
                      )}
                      {device.document_count > 0 && (
                        <span
                          className="attachment-indicator clickable"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAttachmentClick(e, device);
                          }}
                          title="Click to view attached documents"
                        >
                          📎
                          <span className="device-indicator-count device-indicator-count--attachment">
                            {device.document_count}
                          </span>
                        </span>
                      )}
                      {device.active_alert_count > 0 && (
                        <span
                          className="alert-indicator clickable"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onNavigateToAlertsWithFilter) {
                              onNavigateToAlertsWithFilter({
                                device_identifier: device.device_identifier,
                                subject_types: ['device'],
                              });
                            }
                          }}
                          title={`Click to view alerts (${device.active_alert_count} alert${device.active_alert_count !== 1 ? 's' : ''})`}
                        >
                          🔔
                          <span className="device-indicator-count device-indicator-count--alert">
                            {device.active_alert_count}
                          </span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </td>
              <td className="stacked-info">
                <div className="stack-item" style={{ fontWeight: '500', fontSize: '0.9rem', color: '#2c3e50' }}>{device.model}</div>
                <div className="stack-item" style={{ fontWeight: '500', fontSize: '0.8rem', color: '#6c757d' }}>{device.manufacturer}</div>
              </td>
              <td className="stacked-info">
                <div className="stack-item" style={{ fontWeight: '500', fontSize: '0.85rem', color: '#2c3e50' }}>{formatDepartmentWithIcon(device.department, device.department_icon)}</div>
                <div className="stack-item" style={{ fontWeight: '500', fontSize: '0.8rem', color: '#6c757d' }}>{device.physical_location}</div>
              </td>
              <td className="contract-type">
                <div className="contract-info-stack">
                  {device.service_contract_type ? (
                    <span className={`contract-badge contract-badge-${device.service_contract_type.toLowerCase().replace('-', '')}`}>
                      <span className="contract-icon">{getContractTypeIcon(device.service_contract_type).icon}</span>
                      <span className="contract-label">{device.service_contract_type}</span>
                    </span>
                  ) : (
                    <span className="na-text">N/A</span>
                  )}
                  <div className="expiry-dates-stack">
                    {device.warranty_expiry && (
                      <div className="expiry-date-row">
                        <span className="expiry-label">Valid till:</span>
                        <span className="expiry-value">{formatDate(device.warranty_expiry)}</span>
                      </div>
                    )}
                    {device.contract_expiry && (
                      <div className="expiry-date-row">
                        <span className="expiry-label">Valid till:</span>
                        <span className="expiry-value">{formatDate(device.contract_expiry)}</span>
                      </div>
                    )}
                    {!device.warranty_expiry && !device.contract_expiry && (
                      <div className="expiry-date-row">
                        <span className="expiry-placeholder">No expiry dates</span>
                      </div>
                    )}
                  </div>
                </div>
              </td>
              <td className="stacked-info device-cost-cell">
                <div
                  className="stack-item device-cost-primary"
                  title="Device / purchase cost"
                >
                  {device.product_cost != null && device.product_cost !== ''
                    ? formatCurrencyDynamic(device.product_cost)
                    : <span className="na-text">N/A</span>}
                </div>
                <div
                  className="stack-item device-cost-secondary"
                  title="Annual service contract cost"
                >
                  {device.service_contract_cost != null && device.service_contract_cost !== ''
                    ? formatCurrencyDynamic(device.service_contract_cost)
                    : <span className="na-text">N/A</span>}
                </div>
              </td>
              <td className="stacked-info installation-date">
                <div className="stack-item installation-date-primary">
                  {device.installation_date ? (
                    formatDate(device.installation_date)
                  ) : (
                    <span className="na-text">N/A</span>
                  )}
                </div>
                {(() => {
                  const age = getDeviceAgeParts(device);
                  if (!age) return null;
                  const ageText = formatDeviceAgeText(age.years, age.days);
                  return (
                    <div className="stack-item installation-date-age" title={`Device age: ${ageText}`}>
                      <span className="installation-age-emoji" aria-hidden="true">⏱️</span>
                      {ageText}
                    </div>
                  );
                })()}
              </td>
              <td className="stacked-info">
                {device.updated_at ? (
                  <>
                    <div className="stack-item" style={{ fontWeight: '500', fontSize: '0.9rem', color: '#2c3e50' }}>
                      {formatDateTime(device.updated_at).date}
                    </div>
                    <div className="stack-item" style={{ fontWeight: '500', fontSize: '0.8rem', color: '#6c757d' }}>
                      {formatDateTime(device.updated_at).time}
                    </div>
                  </>
                ) : (
                  <span className="na-text">N/A</span>
                )}
              </td>
              <td className="next-due-dates">
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'left', width: '100%' }}>
                  {/* Calendar Icon Only */}
                  {(device.ppm_next_due_date || (device.calibration_required && device.calibration_next_due_date) || (device.compliance_required && device.compliance_next_due_date)) ? (
                    <button
                      className="next-due-icon-btn"
                      onClick={(e) => handleNextDueClick(e, device)}
                      title="View maintenance schedule"
                      style={{ flex: '0 0 auto' }}
                    >
                      <span className="composite-icon">
                        <span className="icon-base">📅</span>
                      </span>
                    </button>
                  ) : (
                    <button
                      className="next-due-icon-btn"
                      disabled
                      style={{ opacity: '0.4', cursor: 'not-allowed', flex: '0 0 auto' }}
                      title="No maintenance schedule configured"
                    >
                      <span className="composite-icon">
                        <span className="icon-base">📅</span>
                      </span>
                    </button>
                  )}
                </div>
              </td>
              <td className="actions" onClick={(e) => e.stopPropagation()}>
                <ActionMenu
                  title="More actions"
                  ariaLabel={`Actions for ${device.name}`}
                  actions={getDeviceActions(device)}
                />
              </td>
              {onToggleDeviceSelection && (
                <td className="device-qr-select-col">
                  <input
                    type="checkbox"
                    checked={selectedDeviceIds && selectedDeviceIds.has(device.id)}
                    onChange={() => onToggleDeviceSelection(device.id)}
                    aria-label={`Select ${device.name} for QR labels`}
                    title={`Select ${device.name} for QR labels`}
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
        {children}
        </div>
    </div>

    {/* Documents Modal - Two Panel Layout */}
    {showDocumentsModal && selectedDeviceForDocuments && ReactDOM.createPortal(
      <div className="modal-overlay">
        <div className="documents-modal">
          <div className="modal-header">
            <h3>Documents - {selectedDeviceForDocuments.name}</h3>
            <button className="close-btn" onClick={handleCloseDocumentsModal}>✕</button>
          </div>

          <div className="documents-modal-content">
            {/* Left Panel - Documents List */}
            <div className="documents-list-panel">
              <div className="documents-list-header">
                <h4>{selectedDeviceDocuments.length} Document{selectedDeviceDocuments.length !== 1 ? 's' : ''}</h4>
              </div>
              {selectedDeviceDocuments.length > 0 ? (
                <div className="documents-list-items">
                  {selectedDeviceDocuments.map((doc) => (
                    <div
                      key={doc.id}
                      className={`document-list-item ${selectedDocumentForPreview?.id === doc.id ? 'active' : ''}`}
                      onClick={() => handlePreviewDocumentInModal(doc, selectedDeviceForDocuments.id)}
                    >
                      <div className="doc-item-icon">{getDocIcon(doc.mime_type)}</div>
                      <div className="doc-item-info">
                        <div className="doc-item-name">{doc.file_name}</div>
                        <div className="doc-item-meta">
                          {formatDocumentType(doc.document_type)} • {doc.file_size ? Math.round(doc.file_size / 1024) : '0'} KB
                        </div>
                      </div>
                      <div className="doc-item-preview-icon">👁️</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-documents">
                  <p>No documents found for this device.</p>
                </div>
              )}
            </div>

            {/* Right Panel - Preview */}
            <div className="documents-preview-panel">
              {selectedDocumentForPreview ? (
                <>
                  <div className="preview-header">
                    <h4>{selectedDocumentForPreview.file_name}</h4>
                    <button
                      className="close-preview-btn"
                      onClick={() => {
                        setSelectedDocumentForPreview(null);
                        if (documentPreviewUrlInModal) {
                          window.URL.revokeObjectURL(documentPreviewUrlInModal);
                        }
                        setDocumentPreviewUrlInModal(null);
                      }}
                      title="Close preview"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="preview-content">
                    {previewLoadingInModal ? (
                      <div className="preview-loading">
                        <div className="spinner"></div>
                        <p>Loading document...</p>
                      </div>
                    ) : documentPreviewUrlInModal ? (
                      <>
                        {selectedDocumentForPreview.mime_type.includes('pdf') ? (
                          <iframe
                            src={documentPreviewUrlInModal}
                            className="pdf-viewer"
                            title="PDF Viewer"
                          ></iframe>
                        ) : selectedDocumentForPreview.mime_type.includes('image') ? (
                          <div className="image-viewer">
                            <img
                              src={documentPreviewUrlInModal}
                              alt={selectedDocumentForPreview.file_name}
                              className="preview-image"
                            />
                          </div>
                        ) : (
                          <div className="document-unavailable">
                            <p>Preview not available for this file type</p>
                            <p className="file-info">{selectedDocumentForPreview.file_name}</p>
                            <button
                              className="btn-download-preview"
                              onClick={() => {
                                const link = document.createElement('a');
                                link.href = documentPreviewUrlInModal;
                                link.download = selectedDocumentForPreview.file_name;
                                link.click();
                              }}
                            >
                              ⬇️ Download File
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="preview-error">
                        <p>Failed to load document</p>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="preview-empty">
                  <p>Select a document to preview</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>,
      document.body
    )}

    {/* Complaints Modal */}
    {showComplaintsModal && ReactDOM.createPortal(
      <div className="modal-overlay" onClick={handleCloseComplaintsModal}>
        <div
          className="complaints-modal"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="active-complaints-modal-title"
        >
          <div className="complaints-modal-header">
            <div className="complaints-modal-heading">
              <h3 id="active-complaints-modal-title">Active Complaints</h3>
              <div className="complaints-modal-subheading">
                <span className="complaints-modal-device-name">{selectedDeviceName}</span>
                {selectedDeviceIdentifier && (
                  <DeviceIDBadge deviceId={selectedDeviceIdentifier} size="small" />
                )}
                <span className="complaints-modal-count">
                  {selectedDeviceComplaints.length} open
                </span>
              </div>
            </div>
            <button
              type="button"
              className="close-btn"
              onClick={handleCloseComplaintsModal}
              aria-label="Close active complaints"
            >
              ✕
            </button>
          </div>

          <div className="complaints-modal-content">
            {selectedDeviceComplaints.length > 0 ? (
              <div className="complaints-list">
                {selectedDeviceComplaints.map((complaint) => {
                  const details = complaint.problem_details?.trim();
                  return (
                    <button
                      type="button"
                      key={complaint.id}
                      className="complaint-item"
                      onClick={() => handleComplaintItemClick(complaint)}
                    >
                      <div className="complaint-item-top">
                        <div className="complaint-item-badges">
                          <StatusBadge status={complaint.status || 'unknown'} size="small" />
                          <SeverityBadge severity={complaint.severity} size="small" />
                        </div>
                        {complaint.complaint_identifier && (
                          <ComplaintIDBadge
                            complaintId={complaint.complaint_identifier}
                            size="small"
                          />
                        )}
                      </div>

                      <div className="complaint-item-title">
                        {complaint.title || 'Untitled complaint'}
                      </div>

                      <div className="complaint-item-meta">
                        <span className="complaint-item-category">
                          {complaint.category_label || '—'}
                        </span>
                        <span className="complaint-item-meta-sep" aria-hidden="true">·</span>
                        <span>
                          Reported {formatDate(complaint.created_at || complaint.breakdown_since)}
                        </span>
                        {complaint.assigned_to && (
                          <>
                            <span className="complaint-item-meta-sep" aria-hidden="true">·</span>
                            <span>Assigned to {complaint.assigned_to}</span>
                          </>
                        )}
                      </div>

                      <p className={`complaint-item-problem${!details ? ' is-empty' : ''}`}>
                        {details || 'No details provided by the reporter.'}
                      </p>

                      <span className="complaint-item-action">View details</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="no-complaints">
                <p>No active complaints found for this device.</p>
              </div>
            )}
          </div>
        </div>
      </div>,
      document.body
    )}

    {/* Use the existing ComplaintView component */}
    {selectedComplaintForDetail && (
      <ComplaintView
        complaintId={selectedComplaintForDetail.id}
        token={token}
        onClose={handleCloseComplaintDetail}
        onUpdate={() => {
          // Refresh the complaints list if needed
        }}
        onViewFullDeviceDetails={onView}
      />
    )}

    {showNextDueModal && selectedDeviceForNextDue && (
      <DeviceMaintenanceScheduleModal
        device={selectedDeviceForNextDue}
        token={token}
        onClose={handleCloseNextDueModal}
      />
    )}

    {/* Export Columns Configuration Modal */}
    <ExportColumnConfigurationModal
      isOpen={showExportColumnsConfig}
      onClose={() => setShowExportColumnsConfig(false)}
      title="Configure Export Columns"
      description="A minimal operational set is pre-selected. Add or remove columns as needed. Excel supports any number of columns; PDF is limited for readable landscape layout."
      columns={exportColumns}
      onColumnsChange={setExportColumns}
      pdfMaxColumns={PDF_EXPORT_MAX_COLUMNS}
    />

    {/* QR Code Display Modal - Using ReactDOM.createPortal */}
    {showQRCode && qrDevice && ReactDOM.createPortal(
      <DeviceQRCode
        device={qrDevice}
        onClose={() => {
          setShowQRCode(false);
          setQRDevice(null);
        }}
      />,
      document.body
    )}
  </>
  );
}

export default DeviceList;
